import { Component } from '@theme/component';

/**
 * NH custom cookie-consent banner + granular preferences panel.
 *
 * WHY THIS EXISTS: Shopify's native cookie banner is correctly configured in
 * admin (Settings → Customer privacy → Cookie banner, all regions) and its
 * asset loads on every page, but its auto-render never fires on this shop —
 * verified 2026-07-27/29 from clean sessions where shouldShowBanner()
 * returned true while zero banner elements existed in the DOM and
 * privacyBanner.loadBanner() rendered nothing. This component renders our own
 * two-layer consent UI (banner + per-category preferences dialog) and writes
 * consent EXCLUSIVELY through Shopify's Customer Privacy API
 * (setTrackingConsent), so Shopify Analytics, checkout, app pixels and the
 * consentId proof-of-consent all stay canonical.
 *
 * v2 fixes (2026-07-29, after live testing by Camille):
 *  - Events are bound directly with addEventListener. The theme's declarative
 *    `on:click` attribute system never attached to this element (it renders
 *    outside the section framework), which left every button dead: choices
 *    were never recorded (banner re-appeared on navigation) and the settings
 *    button did nothing.
 *  - The choice marker cookie is now written SYNCHRONOUSLY on click, before
 *    the async consent API round-trip, so a fast navigation can no longer
 *    lose the choice.
 *  - Self-healing: if the marker exists but Shopify's consent state was lost
 *    (e.g. its cookies were cleared independently), the recorded choice is
 *    re-applied silently on the next page view.
 *  - Fully custom preferences panel (accessible modal dialog) with four
 *    categories mapped to Shopify consent purposes: required (always on),
 *    analytics, preferences (personalization), marketing incl. sale/sharing
 *    of data (marketing + sale_of_data written together).
 *
 * Consent purposes written: { analytics, marketing, preferences, sale_of_data }.
 *
 * Accessibility: banner is a non-modal named region, first in tab order
 * (rendered right after the skip link), does not steal focus (WCAG 3.2.1);
 * the preferences panel is a proper modal dialog — role="dialog",
 * aria-modal, labelled by its title, focus moved in on open and trapped
 * (Tab cycles), Escape closes, focus returns to the opener (WCAG 2.4.3,
 * 2.1.2, 4.1.2). While the banner is visible, its footprint is reserved via
 * scroll-padding so keyboard focus is never entirely obscured (2.4.11).
 */

const MARKER_COOKIE = 'nh_cc';

/**
 * Asymmetric marker lifetimes (2026-07-30).
 *
 * A recorded CONSENT may be relied on for up to 13 months (CNIL ceiling). A
 * recorded REFUSAL does not have to be honoured for that long — CNIL's guidance
 * is that a refusal may be revisited after around six months, and re-asking at
 * that point is not nagging. Storing both for 13 months, as this file did until
 * now, silently threw away every declining visitor for over a year.
 *
 * Consequence when a refusal marker expires: no marker exists, so
 * applyDefaultDenyUntilChoice() writes a fresh refusal (nothing is tracked in
 * the meantime) and the banner asks again. The visitor is never tracked without
 * having said yes.
 */
const CONSENT_MAX_AGE_SECONDS = 34164000; // ~13 months (395 days), CNIL ceiling
const REFUSAL_MAX_AGE_SECONDS = 15724800; // ~6 months (182 days), then re-ask

/**
 * A marker records a refusal when nothing was granted ('d'), or when the choice
 * came from a surface we cannot interpret ('x' — treated as undecided by
 * shouldShow() anyway, so the shorter life is the safer default). A partial
 * choice ('c-XXXX') granted at least one purpose and is treated as consent.
 */
function markerMaxAge(value) {
  return value === 'd' || value === 'x' ? REFUSAL_MAX_AGE_SECONDS : CONSENT_MAX_AGE_SECONDS;
}
const PRIVACY_API_POLL_MS = 250;
const PRIVACY_API_POLL_MAX = 40; // give up after ~10s

/** Marker values: 'a' = accepted all, 'd' = declined all, 'c-XXXX' = custom
 * bits in the order analytics, marketing, preferences, sale_of_data
 * (e.g. 'c-1001'), 'x' = choice made in Shopify's own UI. */

function readMarker() {
  const entry = document.cookie.split(';').find((part) => part.trim().startsWith(`${MARKER_COOKIE}=`));
  return entry ? entry.split('=')[1].trim() : null;
}

function writeMarker(value) {
  document.cookie = `${MARKER_COOKIE}=${value}; max-age=${markerMaxAge(value)}; path=/; SameSite=Lax; Secure`;
}

function choiceToMarker(choice) {
  const bits = [choice.analytics, choice.marketing, choice.preferences, choice.sale_of_data]
    .map((granted) => (granted ? '1' : '0'))
    .join('');
  if (bits === '1111') return 'a';
  if (bits === '0000') return 'd';
  return `c-${bits}`;
}

function markerToChoice(marker) {
  if (marker === 'a') return { analytics: true, marketing: true, preferences: true, sale_of_data: true };
  if (marker === 'd') return { analytics: false, marketing: false, preferences: false, sale_of_data: false };
  if (marker && marker.startsWith('c-') && marker.length === 6) {
    const bits = marker.slice(2);
    return {
      analytics: bits[0] === '1',
      marketing: bits[1] === '1',
      preferences: bits[2] === '1',
      sale_of_data: bits[3] === '1',
    };
  }
  return null;
}

/**
 * Prior-consent (opt-in) versus opt-out jurisdictions — Camille's decision,
 * 2026-07-30, second revision.
 *
 * The first version of the gate applied default-deny worldwide. Correct under
 * GDPR and ePrivacy, but stricter than US law requires, and the cost was
 * measured rather than theoretical: NH's advertising spend is almost entirely
 * US-facing, and blocking pixels before a choice removed most new arrivals from
 * the retargeting pool. CCPA/CPRA and the US state privacy laws operate on an
 * OPT-OUT basis: trackers may load by default provided the visitor is given a
 * clear way to opt out of sale/sharing and Global Privacy Control is honoured.
 * Both exist here — /pages/data-sharing-opt-out is linked in the footer, and
 * applyGpcSignal() below acts on the GPC header.
 *
 * FAILS CLOSED BY DESIGN: this returns true (require prior consent) for every
 * region except the explicitly enumerated opt-out set, and also when the region
 * cannot be determined at all. A new market, a VPN, a region string we have not
 * seen, or the API failing to load all resolve to the stricter treatment. Add a
 * country here only after confirming its law is genuinely opt-out.
 *
 * Deliberately NOT in the opt-out set: Switzerland (nFADP), Canada, Brazil,
 * United Kingdom, EEA. Do not add them for convenience.
 */
const OPT_OUT_COUNTRY_PREFIXES = ['US'];

function requiresPriorConsent(privacy) {
  if (!privacy || typeof privacy.getRegion !== 'function') return true;
  let region;
  try {
    region = privacy.getRegion();
  } catch (_) {
    return true;
  }
  if (typeof region !== 'string' || region.length < 2) return true;
  const country = region.slice(0, 2).toUpperCase();
  return !OPT_OUT_COUNTRY_PREFIXES.includes(country);
}

/**
 * US states where the banner is KEPT even though no law requires it.
 *
 * Camille's decision, 2026-08-06: show the banner only where it is either
 * legally required (opt-in regions) or materially useful as a litigation
 * defence. Everywhere else in the US it comes off.
 *
 * WHY THESE STATES. This list is NOT a compliance requirement — no US state
 * mandates a cookie banner. It is a litigation-risk map. These are the twelve
 * ALL-PARTY (two-party) consent wiretapping states, whose statutes give
 * plaintiffs the architecture to argue that a third-party advertising pixel is
 * an unauthorised party "eavesdropping" on the visitor's communication with the
 * site. Consent is a defence to that claim, and an affirmative click on
 * "Accept all" is the cleanest evidence of it.
 *
 * California (CIPA, Penal Code 630 et seq.) is the epicentre: private right of
 * action, $5,000 statutory damages per violation, no proof of harm required.
 * Florida (FSCA) has near-identical language and now trails only California in
 * volume. Pennsylvania (WESCA), Illinois and Massachusetts carry the rest of
 * the meaningful caseload; a First Circuit ruling argued 6 April 2026 on the
 * crime-tort exception is pending and could move Massachusetts either way.
 *
 * The remaining states here have the statutory basis but little or no observed
 * pixel litigation. They are included deliberately — being over-inclusive costs
 * a banner impression, being under-inclusive costs a lawsuit.
 *
 * HONEST LIMITATION: this list does not eliminate the risk. Plaintiffs
 * increasingly plead the FEDERAL Wiretap Act (ECPA), which can be brought in
 * any district regardless of state law. A state carve-out reduces exposure; it
 * does not remove it. The underlying exposure comes from running six
 * advertising pixels, not from where the banner shows.
 *
 * REVIEW CADENCE: re-verified every month by the nh-ccpa-weekly-audit task,
 * which watches for new states enacting all-party consent statutes, new pixel
 * litigation in states not listed here, and any ruling that materially changes
 * the picture in a state that is. Add a state on the audit's recommendation;
 * remove one only on US counsel's advice.
 */
const US_ALL_PARTY_CONSENT_STATES = [
  'CA', // California — CIPA. Epicentre. $5,000/violation, private right of action.
  'CT', // Connecticut
  'DE', // Delaware
  'FL', // Florida — FSCA, near-identical to CIPA. Second by volume.
  'IL', // Illinois — significant activity
  'MD', // Maryland
  'MA', // Massachusetts — 1st Cir. argument heard 2026-04-06, decision pending
  'MT', // Montana
  'NV', // Nevada
  'NH', // New Hampshire
  'PA', // Pennsylvania — WESCA, significant activity
  'WA', // Washington — also My Health My Data Act, private right of action
];

/**
 * Should the consent banner auto-render for this visitor?
 *
 * True in every opt-in region, and in the US only for the all-party-consent
 * states above. Elsewhere in the US the banner does not appear — collection is
 * lawful pre-choice under the opt-out model, and the visitor keeps every
 * control they had: the permanent "Cookie settings" footer trigger, the
 * /pages/data-sharing-opt-out page, and GPC, all of which are unaffected by
 * this function.
 *
 * FAILS CLOSED: anything unreadable — no API, a throwing getRegion, a region
 * string too short to carry a state code — returns true and the banner shows.
 * A spurious banner costs an impression; a missing one could cost a defence.
 */
function bannerRequiredForRegion(privacy) {
  if (requiresPriorConsent(privacy)) return true;
  if (!privacy || typeof privacy.getRegion !== 'function') return true;
  let region;
  try {
    region = privacy.getRegion();
  } catch (_) {
    return true;
  }
  // Shopify returns e.g. 'USNY' — country then subdivision. Without a
  // subdivision we cannot tell which state, so we show the banner.
  if (typeof region !== 'string' || region.length < 4) return true;
  return US_ALL_PARTY_CONSENT_STATES.includes(region.slice(2, 4).toUpperCase());
}

function nativeBannerVisible() {
  const native = document.querySelector('#shopify-pc__banner');
  if (!native) return false;
  const rect = native.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function getPrivacyApi() {
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  return privacy && typeof privacy.setTrackingConsent === 'function' ? privacy : null;
}

function whenPrivacyApiReady() {
  const immediate = getPrivacyApi();
  if (immediate) return Promise.resolve(immediate);

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
  }

  return new Promise((resolve) => {
    let attempts = 0;
    const poll = window.setInterval(() => {
      const api = getPrivacyApi();
      attempts += 1;
      if (api || attempts >= PRIVACY_API_POLL_MAX) {
        window.clearInterval(poll);
        resolve(api);
      }
    }, PRIVACY_API_POLL_MS);
  });
}

class NhConsentBanner extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.banner = this.querySelector('.nh-consent-banner__inner');
    this.dialog = this.querySelector('.nh-consent-prefs');
    this.backdrop = this.querySelector('.nh-consent-prefs__backdrop');
    this.openerBeforeDialog = null;

    this.bindEvents();

    // A consent write from ANY surface (our UI, Shopify's own dialog, an
    // app) confirms a recorded choice — mark and stand down.
    this.handleConsentCollected = (event) => {
      if (this.selfWrite) return; // our own writes already set a precise marker
      // The API's callback can resolve before it dispatches this event, which
      // would leave selfWrite already false and mis-read one of OUR
      // programmatic writes (default-deny, GPC, self-heal) as a visitor
      // choice — hiding the banner without anyone having chosen. A short
      // window closes that race.
      if (this.suppressCollectedUntil && Date.now() < this.suppressCollectedUntil) return;
      // Same guard for the inline default-deny snippet, which writes consent
      // before this module even loads and therefore cannot use the instance
      // flag above. Without this the snippet's refusal was misread as a
      // visitor choice: marker 'x' was written, the banner hid itself, and
      // consent stayed locked at all-"no" with no way to accept.
      if (window.__nhConsentSuppressCollectedUntil
          && Date.now() < window.__nhConsentSuppressCollectedUntil) return;
      writeMarker('x');
      void event;
      this.hideBanner();
    };
    document.addEventListener('visitorConsentCollected', this.handleConsentCollected);

    // Permanent footer "Cookie settings" trigger(s), present on every page.
    this.handleSettingsTriggerClick = (event) => {
      const trigger = event.target instanceof Element && event.target.closest('.nh-cookie-settings-trigger');
      if (!trigger) return;
      event.preventDefault();
      this.openDialog(trigger);
    };
    document.addEventListener('click', this.handleSettingsTriggerClick);

    whenPrivacyApiReady().then((privacy) => {
      if (!privacy) return; // no consent framework — render nothing
      this.privacy = privacy;
      // Default-deny subsumes the GPC sale-of-data opt-out, so only fall back
      // to the narrower GPC write when no default-deny was needed. Keeping
      // these mutually exclusive guarantees at most ONE programmatic consent
      // write per page view (see the suppressCollectedUntil race note above).
      if (!this.applyDefaultDenyUntilChoice()) this.applyGpcSignal();
      this.selfHeal();
      if (this.shouldShow()) this.show();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visitorConsentCollected', this.handleConsentCollected);
    document.removeEventListener('click', this.handleSettingsTriggerClick);
    if (this.nativeBannerObserver) this.nativeBannerObserver.disconnect();
  }

  /** Direct event binding — see v2 note in the header comment. */
  bindEvents() {
    const on = (selector, handler) => {
      this.querySelectorAll(selector).forEach((el) => el.addEventListener('click', handler));
    };
    on('[data-nh-consent="accept"]', () => this.recordChoice({ analytics: true, marketing: true, preferences: true, sale_of_data: true }));
    on('[data-nh-consent="decline"]', () => this.recordChoice({ analytics: false, marketing: false, preferences: false, sale_of_data: false }));
    on('[data-nh-consent="settings"]', (event) => this.openDialog(event.currentTarget));
    on('[data-nh-consent="save"]', () => this.savePreferences());
    on('[data-nh-consent="close"]', () => this.closeDialog());

    if (this.backdrop) this.backdrop.addEventListener('click', () => this.closeDialog());

    this.handleDialogKeydown = (event) => {
      if (!this.dialogOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
        return;
      }
      if (event.key !== 'Tab') return;
      // Focus trap (WCAG 2.1.2 with an exit — Escape and the close button)
      const focusables = [...this.dialog.querySelectorAll(
        'button, [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', this.handleDialogKeydown);
  }

  /**
   * Denied-by-default in EVERY region, until the visitor actually chooses.
   *
   * WHY: Shopify only enforces opt-in where a regulation demands it. Verified
   * live 2026-07-30 from a US vantage (getRegion() === 'USNY'): on a clean
   * first page view currentVisitorConsent() returns "yes" for all four
   * purposes while the banner is still on screen, so every app pixel — GA4,
   * Meta, TikTok, Snapchat, Pinterest, Klaviyo — fires before the visitor has
   * answered. Refusal itself works correctly (verified: zero marketing
   * cookies return after "Decline all"); it is only the pre-choice default
   * that leaks. Writing an explicit refusal while no marker exists makes
   * behaviour uniform worldwide: nothing non-essential runs until acceptance.
   *
   * This is NOT a recorded choice — no marker cookie is written, so the
   * banner still renders and shouldShow() is untouched. Accepting later
   * simply overwrites these values.
   *
   * LIMITATION: this module is deferred, so it runs after Shopify's pixel
   * bootstrap. Shopify's web pixels re-read consent per event, so this blocks
   * the great majority of pre-choice collection but is mitigation, not a hard
   * guarantee for the very first events of a session. An airtight version
   * needs the same write in a blocking inline script ahead of Shopify's
   * scripts in layout/theme.liquid.
   *
   * @returns {boolean} true if a default refusal was written.
   */
  applyDefaultDenyUntilChoice() {
    if (readMarker()) return false; // visitor already chose — never override
    if (!requiresPriorConsent(this.privacy)) return false; // opt-out jurisdiction
    const consent = typeof this.privacy.currentVisitorConsent === 'function'
      ? this.privacy.currentVisitorConsent()
      : null;
    if (!consent) return false;
    // Only write when something is not already refused, so repeat page views
    // of an undecided visitor stay silent.
    const purposes = ['analytics', 'marketing', 'preferences', 'sale_of_data'];
    if (purposes.every((purpose) => consent[purpose] === 'no')) return false;

    this.selfWrite = true;
    this.suppressCollectedUntil = Date.now() + 5000;
    this.privacy.setTrackingConsent(
      { analytics: false, marketing: false, preferences: false, sale_of_data: false },
      () => {
        this.selfWrite = false;
      },
    );
    return true;
  }

  /**
   * GPC is a legally binding opt-out-of-sale signal. Apply it once, silently,
   * without recording a full consent choice — the visitor still gets the
   * banner for the remaining purposes.
   */
  applyGpcSignal() {
    if (navigator.globalPrivacyControl !== true) return;
    if (readMarker()) return;
    const consent = typeof this.privacy.currentVisitorConsent === 'function'
      ? this.privacy.currentVisitorConsent()
      : null;
    if (consent && consent.sale_of_data === '') {
      this.selfWrite = true;
      this.suppressCollectedUntil = Date.now() + 5000;
      this.privacy.setTrackingConsent({ sale_of_data: false }, () => {
        this.selfWrite = false;
      });
    }
  }

  /**
   * If our marker records a choice but Shopify's consent state looks fresh
   * (its own cookies were cleared independently), re-apply the recorded
   * choice so both stores agree.
   */
  selfHeal() {
    const choice = markerToChoice(readMarker());
    if (!choice) return;
    const consent = typeof this.privacy.currentVisitorConsent === 'function'
      ? this.privacy.currentVisitorConsent()
      : null;
    if (consent && consent.sale_of_data === '') {
      this.selfWrite = true;
      this.suppressCollectedUntil = Date.now() + 5000;
      this.privacy.setTrackingConsent(choice, () => {
        this.selfWrite = false;
      });
    }
  }

  shouldShow() {
    // Region gate first — see bannerRequiredForRegion(). In a US state that is
    // not all-party consent, the banner never auto-renders, including for the
    // 'x' marker case below. This does NOT weaken anyone's controls: the footer
    // "Cookie settings" trigger still opens the preferences dialog, GPC is still
    // applied by applyGpcSignal(), /pages/data-sharing-opt-out still works, and
    // selfHeal() still re-applies a previously recorded refusal. Someone who
    // declined before this shipped stays declined.
    if (!bannerRequiredForRegion(this.privacy)) return false;

    const marker = readMarker();
    // 'x' means "a choice was recorded on some other surface" and carries no
    // information about WHAT was chosen. On this shop no other surface exists
    // (Shopify's own banner never renders), so in practice an 'x' is the
    // signature of the 2026-07-30 defect where the inline default-deny write
    // was misread as a visitor choice. Treat it as undecided so anyone
    // wrongly marked gets asked properly rather than being silently held at
    // full refusal for 13 months. If Shopify's native banner is ever fixed,
    // watchForNativeBanner() still prevents a double banner.
    if (marker === 'x') return true;
    if (marker) return false;
    if (nativeBannerVisible()) return false;
    if (typeof this.privacy.shouldShowBanner === 'function' && !this.privacy.shouldShowBanner()) {
      return false;
    }
    return true;
  }

  show() {
    this.removeAttribute('hidden');
    this.watchForNativeBanner();
    // WCAG 2.4.11 Focus Not Obscured: reserve the banner's footprint in the
    // scroll box so focused elements always scroll clear of it. Injected as
    // a dedicated <style> element rather than an inline style on <html>,
    // because the theme's drawer scroll-lock rewrites the html style
    // attribute wholesale and would wipe an inline property (observed live
    // 2026-07-29).
    window.requestAnimationFrame(() => {
      if (!this.banner) return;
      const reserve = Math.ceil(this.banner.getBoundingClientRect().height) + 24;
      let pad = document.getElementById('nh-consent-scroll-pad');
      if (!pad) {
        pad = document.createElement('style');
        pad.id = 'nh-consent-scroll-pad';
        document.head.appendChild(pad);
      }
      pad.textContent = `html { scroll-padding-block-end: ${reserve}px; }`;
    });
  }

  hideBanner() {
    this.setAttribute('hidden', '');
    const pad = document.getElementById('nh-consent-scroll-pad');
    if (pad) pad.remove();
    if (this.nativeBannerObserver) {
      this.nativeBannerObserver.disconnect();
      this.nativeBannerObserver = null;
    }
  }

  /**
   * Persist a choice. The marker cookie is written synchronously so a fast
   * navigation cannot lose the decision; the Customer Privacy API write
   * completes in the background (and self-heals next page view if it
   * didn't finish).
   */
  recordChoice(choice) {
    writeMarker(choiceToMarker(choice));
    this.closeDialog();
    this.hideBanner();
    if (!this.privacy) return;
    this.selfWrite = true;
    this.privacy.setTrackingConsent(choice, () => {
      this.selfWrite = false;
      document.dispatchEvent(new CustomEvent('nh:consent-choice', { detail: { choice } }));
    });
  }

  /** Read the four toggles and persist as a custom choice. */
  savePreferences() {
    const value = (purpose) => {
      const input = this.dialog.querySelector(`input[data-nh-purpose="${purpose}"]`);
      return input ? input.checked : false;
    };
    const marketing = value('marketing');
    this.recordChoice({
      analytics: value('analytics'),
      preferences: value('preferences'),
      marketing,
      // Sale/sharing of data rides with the marketing category — one clear
      // toggle for the visitor, both purposes written for compliance.
      sale_of_data: marketing,
    });
  }

  openDialog(opener) {
    if (!this.dialog) return;
    this.openerBeforeDialog = opener instanceof HTMLElement ? opener : null;
    // Pre-check toggles from the current consent state (or defaults: all off)
    const consent = this.privacy && typeof this.privacy.currentVisitorConsent === 'function'
      ? this.privacy.currentVisitorConsent()
      : null;
    ['analytics', 'marketing', 'preferences'].forEach((purpose) => {
      const input = this.dialog.querySelector(`input[data-nh-purpose="${purpose}"]`);
      if (input && consent) input.checked = consent[purpose] === 'yes';
    });
    this.removeAttribute('hidden'); // host must be visible to show the dialog
    this.dialog.removeAttribute('hidden');
    if (this.backdrop) this.backdrop.removeAttribute('hidden');
    this.dialogOpen = true;
    const title = this.dialog.querySelector('.nh-consent-prefs__title');
    if (title) title.focus();
  }

  closeDialog() {
    if (!this.dialog || !this.dialogOpen) return;
    this.dialog.setAttribute('hidden', '');
    if (this.backdrop) this.backdrop.setAttribute('hidden', '');
    this.dialogOpen = false;
    // If no choice is recorded yet, the banner stays; otherwise hide the host.
    if (readMarker()) {
      this.hideBanner();
    }
    if (this.openerBeforeDialog && document.contains(this.openerBeforeDialog)) {
      this.openerBeforeDialog.focus(); // WCAG: focus returns to the opener
    }
    this.openerBeforeDialog = null;
  }

  /**
   * Double-banner guard: if Shopify's broken auto-render is ever fixed
   * server-side and its banner appears while ours is on screen, yield —
   * hide ours without recording a choice.
   */
  watchForNativeBanner() {
    if (this.nativeBannerObserver || typeof MutationObserver !== 'function') return;
    this.nativeBannerObserver = new MutationObserver(() => {
      if (nativeBannerVisible() && !this.dialogOpen) this.hideBanner();
    });
    this.nativeBannerObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
}

if (!customElements.get('nh-consent-banner')) {
  customElements.define('nh-consent-banner', NhConsentBanner);
}
